/**
 * Server-sent events over `fetch`.
 *
 * `EventSource` is not usable here: it cannot send an `Authorization` header, it
 * cannot POST, and every streaming route in this API is a POST with a body. So the
 * stream is read straight off the response, which also means the 401-refresh path in
 * `ApiClient.fetchAuthed` applies to streams exactly as it does to ordinary calls.
 *
 * The backend emits bare `data: {json}\n\n` frames with no `event:` names and no ids,
 * so the parser is deliberately narrow — it handles what the API sends, not the whole
 * SSE grammar. It does handle the two things that actually bite: a frame split across
 * TCP chunk boundaries, and multi-line `data:` continuations.
 */

import type { ApiClient, RequestOptions } from './client'
import { errorFrom } from './client'

export interface StreamOptions extends Omit<RequestOptions, 'method'> {
  signal?: AbortSignal
}

/**
 * POSTs and yields each decoded frame as it arrives.
 *
 * Errors surface as thrown `ApiError`s only for the *request* — a 429 when the daily
 * AI budget is spent, say. Failures inside the stream arrive as ordinary frames with
 * their own error fields, because by then the response is already a 200 and the
 * partial answer is worth keeping.
 */
export async function* streamFrames<T>(
  client: ApiClient,
  path: string,
  body: unknown,
  options: StreamOptions = {},
): AsyncGenerator<T, void, undefined> {
  const response = await client.fetchAuthed(path, {
    ...options,
    method: 'POST',
    body,
    headers: { Accept: 'text/event-stream', ...options.headers },
  })

  if (!response.ok) throw await errorFrom(response)
  if (!response.body) throw new Error('The server returned no stream.')

  const reader = response.body.pipeThrough(new TextDecoderStream()).getReader()
  let buffer = ''

  try {
    for (;;) {
      const { done, value } = await reader.read()
      if (done) break

      buffer += value

      // Frames are separated by a blank line. Anything after the last separator is
      // an incomplete frame and stays in the buffer for the next chunk.
      let separator = buffer.indexOf('\n\n')
      while (separator !== -1) {
        const frame = buffer.slice(0, separator)
        buffer = buffer.slice(separator + 2)

        const decoded = decodeFrame<T>(frame)
        if (decoded !== undefined) yield decoded

        separator = buffer.indexOf('\n\n')
      }
    }

    // A stream that ends without its trailing blank line still carried a frame.
    const trailing = decodeFrame<T>(buffer)
    if (trailing !== undefined) yield trailing
  } finally {
    // Abandoning a generator early (a student navigating away mid-answer) must not
    // leave the socket open. The server treats the disconnect as a cancel and
    // rescues whatever it had already written.
    await reader.cancel().catch(() => {})
  }
}

/** One frame's `data:` payload, JSON-decoded. `undefined` for comments and blanks. */
function decodeFrame<T>(frame: string): T | undefined {
  const data = frame
    .split('\n')
    .filter((line) => line.startsWith('data:'))
    // Exactly one leading space is part of the field separator, not the value.
    .map((line) => line.slice(5).replace(/^ /, ''))
    .join('\n')

  if (!data) return undefined

  try {
    return JSON.parse(data) as T
  } catch {
    // A malformed frame is not worth killing a half-written answer over.
    return undefined
  }
}

// ---------------------------------------------------------------------------
// Frame shapes, one per streaming route
// ---------------------------------------------------------------------------

/** `POST /chats/stream` and `POST /chats/{id}/messages/stream`.
 *  `text` is a delta to append, not the whole answer so far. */
export interface ChatStreamFrame {
  id: string
  text: string
  is_error: boolean
  done: boolean
  /** Only on the frame where the thread was auto-named. */
  title?: string
  content_format?: string
  format_version?: number
}

/** `POST /tests/generate/stream` and `POST /tests/quick-revise/start/stream`.
 *
 * Three frame kinds share one shape:
 * - the Quick Revise opener, carrying `session_id` and `source_summary`
 * - a question, with `origin` saying whether it came from the bank or was just written
 * - the terminal `done` frame, whose `error` is set only when nothing was produced
 */
export interface QuestionStreamFrame<Q = unknown, S = unknown> {
  question?: Q | null
  origin?: 'bank' | 'generated'
  done: boolean
  error?: string | null
  session_id?: string
  source_summary?: S
}
